import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

/**
 * Custom hook for previewing files
 * 
 * SUPPORTED PREVIEW TYPES (Backend Limitations):
 * - Images: JPEG, PNG, GIF (native browser support)
 * - Documents: PDF, Text files (native browser support)
 * 
 * IMPORTANT: Microsoft Office files (PPT, PPTX, DOC, DOCX, XLS, XLSX) are NOT supported 
 * for preview by the backend API. These files can only be downloaded.
 * 
 * @param {string} persona - The user persona ('customer' or 'admin'/'super_admin')
 * @returns {object} - { previewFile, previewing, error, fileInfo, previewUrl, clearPreview, isPreviewSupported }
 */
export const useFilePreview = (persona = 'customer') => {
  const [previewing, setPreviewing] = useState(false);
  const [error, setError] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [currentFileId, setCurrentFileId] = useState(null);

  /**
   * Clean up the current preview URL to prevent memory leaks
   */
  const clearPreview = useCallback(() => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setFileInfo(null);
    setCurrentFileId(null);
    setError(null);
  }, [previewUrl]);

  /**
   * Clean up on unmount
   */
  useEffect(() => {
    return () => {
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  /**
   * Preview a file by ID
   * @param {number|string} fileId - The file ID to preview
   * @returns {Promise<boolean>} - Success status
   */
  const previewFile = useCallback(async (fileId) => {
    if (!fileId) {
      console.error('File ID is required for preview');
      setError('File ID is required for preview');
      return false;
    }

    // If previewing the same file, return early
    if (currentFileId === fileId && previewUrl) {
      return true;
    }

    // Clear previous preview
    clearPreview();

    try {
      setPreviewing(true);
      setError(null);
      setCurrentFileId(fileId);

      // Determine endpoint based on persona
      const endpoint = persona === 'customer' 
        ? `/api/files/preview/${fileId}`
        : `/api/files/admin/preview/${fileId}`;

      const response = await axiosInstance.get(endpoint, {
        responseType: 'blob',
      });

      // Get file info from headers
      let parsedFileInfo = null;
      const fileInfoHeader = response.headers['x-file-info'];
      if (fileInfoHeader) {
        try {
          parsedFileInfo = JSON.parse(fileInfoHeader);
        } catch (e) {
          console.warn('Could not parse file info from headers:', e);
        }
      }

      // Create blob URL for preview
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream'
      });
      const url = window.URL.createObjectURL(blob);

      // Set state with preview data
      setPreviewUrl(url);
      setFileInfo(parsedFileInfo);

      return true;
    } catch (err) {
      console.error('Error previewing file:', err);
      let errorMessage = 'Failed to preview file';
      
      if (err.response?.status === 400) {
        errorMessage = 'File type not supported for preview';
      } else if (err.response?.status === 404) {
        errorMessage = 'File not found';
      } else if (err.response?.status === 401) {
        errorMessage = 'Unauthorized access';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      
      setError(errorMessage);
      setCurrentFileId(null);
      return false;
    } finally {
      setPreviewing(false);
    }
  }, [persona, currentFileId, previewUrl, clearPreview]);

  /**
   * Check if a file type is supported for preview
   * @param {string} mimeType - The MIME type to check
   * @returns {object} - Detailed support information
   * @returns {boolean} returns.isSupported - Whether file type is supported by backend for upload
   * @returns {boolean} returns.isPreviewable - Whether file can be previewed in browser (ONLY images, PDFs, text)
   * @returns {boolean} returns.isCompressed - Whether file is a compressed archive  
   * @returns {boolean} returns.isOfficeFile - Whether file is a Microsoft Office document
   * @returns {boolean} returns.recommendDownload - Whether download is recommended over preview
   */
  const isPreviewSupported = useCallback((mimeType) => {
    if (!mimeType) return {
      isSupported: false,
      isPreviewable: false,
      isCompressed: false,
      isOfficeFile: false,
      recommendDownload: false
    };
    
    const previewableTypes = [
      // Images (native browser support) - CONFIRMED BY API
      'image/jpeg',
      'image/jpg', 
      'image/png',
      'image/gif',
      
      // Documents (native browser support) - CONFIRMED BY API
      'application/pdf',
      'text/plain',
      
      // Additional text types commonly supported by browsers
      'text/csv',
      'text/html',
      'text/xml',
      'text/css',
      'text/javascript',
      'application/json',
    ];
    
    // Microsoft Office files - NOT SUPPORTED BY BACKEND API (download only)
    const officeTypes = [
      'application/vnd.ms-powerpoint', // .ppt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
      'application/vnd.ms-excel', // .xls
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    ];

    // Compressed files are supported for upload but not for preview
    const compressedTypes = [
      'application/zip',
      'application/x-zip-compressed',
      'application/vnd.rar',
      'application/x-rar-compressed',
      'application/x-7z-compressed',
      'application/x-tar',
      'application/gzip',
      'application/x-bzip2',
      'application/x-xz'
    ];
    
    const normalizedMimeType = mimeType.toLowerCase();
    
    // Check if it's a previewable type (only images and PDFs/text)
    const isPreviewable = previewableTypes.some(type => normalizedMimeType.includes(type));
    
    // Check if it's a compressed file
    const isCompressed = compressedTypes.some(type => normalizedMimeType.includes(type));
    
    // Check if it's an Office file 
    const isOfficeFile = officeTypes.some(type => normalizedMimeType.includes(type));
    
    return {
      isSupported: isPreviewable || isCompressed || isOfficeFile,
      isPreviewable: isPreviewable, // Only true for images, PDFs, and text files
      isCompressed: isCompressed,
      isOfficeFile: isOfficeFile,
      recommendDownload: isCompressed || isOfficeFile // Office files also recommend download
    };
  }, []);

  /**
   * Simple boolean check for backward compatibility
   * @param {string} mimeType - The MIME type to check
   * @returns {boolean} - Whether the file can be previewed
   */
  const canPreviewFile = useCallback((mimeType) => {
    const support = isPreviewSupported(mimeType);
    return support?.isPreviewable || false;
  }, [isPreviewSupported]);

  return {
    // State
    previewing,
    error,
    fileInfo,
    previewUrl,
    currentFileId,
    
    // Actions
    previewFile,
    clearPreview,
    
    // File type checking
    isPreviewSupported, // Returns detailed object
    canPreviewFile,     // Returns simple boolean
  };
};

export default useFilePreview;
